import { useEffect, useMemo, useState } from 'react';
import { useOrgInventory } from '../../../hooks/useOrgInventory';
import type { Organization, OrgInventoryItem, QuoteType } from '../../../types/admin';
import { LoadingSpinner } from '../../../components/ui/LoadingSpinner';
import { isPricedCategory, parseRentalDaysInput, parseUnitCostInput } from '../../../lib/inventoryPricing';

// ── Types ────────────────────────────────────────────────────────────────

/** One edited material, ready to PATCH to the org inventory. */
export interface MaterialPriceChange {
  id: string;
  unitCost: number | null;
  rentalDays: number | null;
}

export interface MaterialsPricingData {
  /** Only the items whose cost or rental length differs from what is saved. */
  changes: MaterialPriceChange[];
  /** Names of items with a rental length but no cost (the API rejects that pair). */
  rentalWithoutCost: string[];
  /** Fields that aren't a valid amount / whole number of days. */
  invalidFields: string[];
  /** The catalog loaded, so the admin actually saw the prices they are confirming. */
  loaded: boolean;
}

interface MaterialsPricingStepProps {
  org: Organization;
  orgId: string;
  onChange: (data: MaterialsPricingData) => void;
  /** Highlight rows that block Continue (set after a blocked attempt). */
  showErrors?: boolean;
}

interface Draft {
  cost: string;
  days: string;
}

const CATEGORY_LABEL: Record<'packing_item' | 'equipment', string> = {
  packing_item: 'Packing Items',
  equipment: 'Equipment',
};

function toDraft(item: OrgInventoryItem): Draft {
  return {
    cost: item.unitCost != null ? String(item.unitCost) : '',
    days: item.rentalDays != null ? String(item.rentalDays) : '',
  };
}

/** A typed value the parser can't read (e.g. "abc", "-2", "1.5 days"). Blank is valid. */
function costIsInvalid(text: string): boolean {
  return text.trim() !== '' && parseUnitCostInput(text) === null;
}

function daysAreInvalid(text: string): boolean {
  const trimmed = text.trim();
  return trimmed !== '' && (parseRentalDaysInput(trimmed) === null || !/^\d+$/.test(trimmed));
}

// ── Main component ───────────────────────────────────────────────────────

export function MaterialsPricingStep({ org, orgId, onChange, showErrors = false }: MaterialsPricingStepProps) {
  const { items, loading, error } = useOrgInventory(orgId);
  const [drafts, setDrafts] = useState<Record<string, Draft>>({});

  const moveTypes = useMemo<QuoteType[]>(() => {
    const types: QuoteType[] = [];
    if (org.featureCommercialMovingQuotes === true) types.push('commercial');
    if (org.featureResidentialMovingQuotes === true) types.push('residential');
    return types;
  }, [org.featureCommercialMovingQuotes, org.featureResidentialMovingQuotes]);

  const materials = useMemo(
    () => items
      .filter(i => isPricedCategory(i.residentialType) && moveTypes.includes(i.quoteType ?? 'commercial'))
      .sort((a, b) => a.name.localeCompare(b.name)),
    [items, moveTypes],
  );

  // Seed drafts from the saved catalog once it loads.
  useEffect(() => {
    if (loading) return;
    setDrafts(prev => {
      const next = { ...prev };
      for (const item of materials) if (!next[item.id]) next[item.id] = toDraft(item);
      return next;
    });
  }, [loading, materials]);

  // Report the edits (and anything blocking) to the wizard.
  useEffect(() => {
    const changes: MaterialPriceChange[] = [];
    const rentalWithoutCost: string[] = [];
    const invalidFields: string[] = [];
    for (const item of materials) {
      const draft = drafts[item.id];
      if (!draft) continue;
      if (costIsInvalid(draft.cost)) { invalidFields.push(`${item.name} cost`); continue; }
      if (daysAreInvalid(draft.days)) { invalidFields.push(`${item.name} rental length`); continue; }
      const unitCost = parseUnitCostInput(draft.cost);
      const rentalDays = parseRentalDaysInput(draft.days);
      if (rentalDays != null && unitCost == null) { rentalWithoutCost.push(item.name); continue; }
      if (unitCost !== (item.unitCost ?? null) || rentalDays !== (item.rentalDays ?? null)) {
        changes.push({ id: item.id, unitCost, rentalDays });
      }
    }
    onChange({ changes, rentalWithoutCost, invalidFields, loaded: !loading && !error });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [drafts, materials, loading, error]);

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-xl border border-rose/40 bg-white p-4 text-small text-rose">
        Your materials couldn't be loaded. You can skip this step and set prices later under
        Admin Panel → Quoting → Inventory Items.
      </div>
    );
  }

  const noCostCount = materials.filter(i => !parseUnitCostInput(drafts[i.id]?.cost ?? '')).length;
  const groups = moveTypes.flatMap(type => (['packing_item', 'equipment'] as const).map(category => ({
    key: `${type}-${category}`,
    title: moveTypes.length > 1
      ? `${type === 'commercial' ? 'Commercial' : 'Residential'} · ${CATEGORY_LABEL[category]}`
      : CATEGORY_LABEL[category],
    items: materials.filter(i => (i.quoteType ?? 'commercial') === type && i.residentialType === category),
  }))).filter(g => g.items.length > 0);

  function setDraft(id: string, patch: Partial<Draft>) {
    setDrafts(prev => ({ ...prev, [id]: { ...(prev[id] ?? { cost: '', days: '' }), ...patch } }));
  }

  return (
    <div className="space-y-4">
      <section className="bg-white border border-stone rounded-xl shadow-brand-sm p-4">
        <p className="font-semibold text-small text-midnight">What your materials cost</p>
        <p className="text-[12px] text-slate leading-snug mt-1">
          Crates, cartons, carts and supplies are added to each estimate's Materials total at these prices.
          Enter what your company charges for each one.
        </p>
        <ul className="text-[12px] text-slate leading-snug mt-2 space-y-1 list-disc pl-4">
          <li><span className="font-medium text-midnight">Unit Cost</span>: leave it blank if you don't charge for the item.</li>
          <li>
            <span className="font-medium text-midnight">Rental Length</span>: days per rental period for items you rent
            and take back (e.g. $3 per 10 days). Leave it blank for items the customer keeps.
          </li>
          <li>Each quote keeps the prices it was started with, so changes here apply to items added to quotes from now on.</li>
        </ul>
        {materials.length > 0 && noCostCount > 0 && (
          <p className="mt-3 inline-flex items-center rounded-full bg-amber-100 px-2.5 py-1 text-[12px] font-medium text-amber-800">
            {noCostCount} of {materials.length} without a cost
          </p>
        )}
      </section>

      {materials.length === 0 && (
        <div className="rounded-xl border border-stone bg-mist p-4 text-small text-slate">
          Your catalog has no packing items or equipment yet. You can add them later under
          Admin Panel → Quoting → Inventory Items.
        </div>
      )}

      {groups.map(group => (
        <section key={group.key} className="bg-white border border-stone rounded-xl shadow-brand-sm overflow-hidden">
          <div className="px-4 py-3 border-b border-stone">
            <p className="font-semibold text-small text-midnight">{group.title}</p>
          </div>
          <div className="px-4">
            {group.items.map(item => (
              <MaterialPriceRow
                key={item.id}
                item={item}
                draft={drafts[item.id] ?? toDraft(item)}
                onChange={patch => setDraft(item.id, patch)}
                showErrors={showErrors}
              />
            ))}
          </div>
        </section>
      ))}
    </div>
  );
}

// ── Row: cost and rental length, always inputs (no edit mode) ────────────

function MaterialPriceRow({ item, draft, onChange, showErrors }: {
  item: OrgInventoryItem;
  draft: Draft;
  onChange: (patch: Partial<Draft>) => void;
  showErrors: boolean;
}) {
  const badCost = costIsInvalid(draft.cost);
  const badDays = daysAreInvalid(draft.days);
  const rentalWithoutCost = !badCost && !badDays
    && parseRentalDaysInput(draft.days) != null && parseUnitCostInput(draft.cost) == null;
  const message = badCost
    ? 'Enter an amount like 3 or 3.50.'
    : badDays
      ? 'Enter a whole number of days, or leave it blank.'
      : rentalWithoutCost
        ? 'Add a unit cost, or clear the rental length.'
        : null;
  const highlight = showErrors && message !== null;
  const costId = `material-cost-${item.id}`;
  const daysId = `material-days-${item.id}`;
  const inputBase = `h-11 px-3 rounded-lg border bg-white text-small text-midnight text-right font-mono tabular-nums
                     placeholder:text-slate focus:outline-none focus:ring-2 focus:ring-plum/20 transition-colors duration-150`;

  return (
    <div className="py-3 border-b border-stone last:border-b-0">
      <p className="text-small font-medium text-midnight">{item.name}</p>
      <div className="mt-2 flex items-center gap-3">
        <div className="flex items-center gap-1.5">
          <label htmlFor={costId} className="sr-only">{item.name} unit cost</label>
          <span className="text-small text-slate">$</span>
          <input
            id={costId}
            type="text"
            inputMode="decimal"
            placeholder="No cost"
            value={draft.cost}
            onChange={e => onChange({ cost: e.target.value })}
            aria-invalid={(highlight && (badCost || rentalWithoutCost)) || undefined}
            className={`w-24 ${inputBase} ${highlight && (badCost || rentalWithoutCost) ? 'border-rose focus:border-rose' : 'border-stone focus:border-plum'}`}
          />
        </div>
        <div className="flex items-center gap-1.5">
          <label htmlFor={daysId} className="text-[12px] text-slate">per</label>
          <input
            id={daysId}
            type="text"
            inputMode="numeric"
            placeholder="—"
            value={draft.days}
            onChange={e => onChange({ days: e.target.value })}
            aria-invalid={(highlight && badDays) || undefined}
            className={`w-16 ${inputBase} ${highlight && badDays ? 'border-rose focus:border-rose' : 'border-stone focus:border-plum'}`}
          />
          <span className="text-[12px] text-slate">days</span>
        </div>
      </div>
      {message && (
        <p className={`text-[12px] mt-1.5 ${highlight ? 'text-rose' : 'text-slate'}`}>{message}</p>
      )}
    </div>
  );
}
